import React from 'react';
import Button, { type ButtonProps } from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

export interface PrimaryButtonProps extends Omit<ButtonProps, 'variant'> {
  loading?: boolean;
}

/**
 * THE primary action button — used across 36 files.
 *
 * Paints from Tier-2 tokens, not `theme.button.primary`. The legacy theme
 * object is per-preset; the tokens are the design system
 * (`future/design-system-basil.md`: "Components reference ONLY Tier 2 semantic
 * tokens"). Reading the object meant every one of those 36 files followed
 * whichever preset a device happened to hold, which is how primary actions
 * ended up rendering blue.
 */
const PrimaryButton: React.FC<PrimaryButtonProps> = ({
  children,
  loading = false,
  disabled,
  sx,
  ...props
}) => {
  return (
    <Button
      variant="contained"
      disabled={disabled || loading}
      sx={{
        minHeight: 44,
        backgroundColor: 'var(--action)',
        color: 'var(--action-text)',
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '6px',
        whiteSpace: 'nowrap',
        // Allow buttons to shrink inside flex containers when viewport is narrow
        // (mobile dialogs were horizontally scrolling because flexShrink:0 forced
        // the button to keep its intrinsic width even with fullWidth=true).
        minWidth: 0,
        '&:hover': {
          backgroundColor: 'var(--action-hover)',
        },
        '&:active': {
          backgroundColor: 'var(--action-press)',
        },
        '&.Mui-disabled': {
          backgroundColor: 'var(--bg-sunken)',
          color: 'var(--text-disabled)',
        },
        ...sx,
      }}
      {...props}
    >
      {loading ? (
        <CircularProgress size={20} color="inherit" sx={{ mr: children ? 1 : 0 }} />
      ) : null}
      {children}
    </Button>
  );
};

export default PrimaryButton;
